/**
 * Firestore service for the contact-lens order collection (Sprint 3).
 *
 * Collection path: `clinics/{clinicId}/lensOrders/{orderId}`
 *
 * Invariants enforced by this layer (the UI must never bypass them):
 *   - createdAt/updatedAt/createdBy/updatedBy are stamped server-side (from
 *     the perspective of the caller: the service writes them, never the UI)
 *   - `sku` is always a snapshot of a `LensCatalogEntry` — catalog is mutable
 *     (quarterly refreshes may retire an EAN) so every order keeps its own
 *     copy for audit purposes even years later
 *   - Status transitions go CONFIRMED → {DELIVERED | CANCELLED} only. You
 *     cannot uncancel, undeliver, or re-confirm. The `transitionStatus()`
 *     helper guards this.
 *
 * The generic audit trigger (`onClinicDocumentWritten`) picks up writes here
 * automatically once `'lensOrders'` is added to `AUDITED_COLLECTIONS`.
 */

import { db } from './config';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as qLimit,
} from 'firebase/firestore';
import type {
  LensCatalogEntry,
  LensOrder,
  LensOrderFulfillment,
  LensOrderSku,
  LensOrderStatus,
  SignatureRecord,
} from '@/types/lc';
import { stripUndefined } from './utils';

const COLLECTION = 'lensOrders';

function colRef(clinicId: string) {
  return collection(db, 'clinics', clinicId, COLLECTION);
}

function docRef(clinicId: string, orderId: string) {
  return doc(db, 'clinics', clinicId, COLLECTION, orderId);
}

/**
 * Snapshot a catalog entry into the denormalized shape stored on the order.
 *
 * We do NOT carry over `ean → id`, batch tracking fields, status, createdAt,
 * etc. — only the clinically relevant product data. This keeps the order doc
 * stable even if the catalog entry is later re-ingested with a different
 * `updatedAt` or `sourceBatchId`.
 */
export function snapshotSku(entry: LensCatalogEntry): LensOrderSku {
  return {
    ean: entry.ean,
    familyId: entry.familyId,
    familyName: entry.familyName,
    prefix: entry.prefix,
    tipo: entry.tipo,
    bc: entry.bc,
    sph: entry.sph,
    cyl: entry.cyl,
    axis: entry.axis,
    dia: entry.dia,
    add: entry.add,
    marker: entry.marker,
    descricao: entry.descricao,
  };
}

/**
 * Allowed status transitions. Anything not listed here is rejected.
 *
 *   CONFIRMED → DELIVERED
 *   CONFIRMED → CANCELLED
 *
 * DELIVERED and CANCELLED are terminal — you create a new order if the
 * patient comes back for a different SKU.
 */
const ALLOWED_TRANSITIONS: Record<LensOrderStatus, LensOrderStatus[]> = {
  CONFIRMED: ['DELIVERED', 'CANCELLED'],
  DELIVERED: [],
  CANCELLED: [],
};

function assertTransition(from: LensOrderStatus, to: LensOrderStatus): void {
  const allowed = ALLOWED_TRANSITIONS[from] ?? [];
  if (!allowed.includes(to)) {
    throw new Error(
      `Transição de status inválida: ${from} → ${to}. Permitidas a partir de ${from}: ${
        allowed.length ? allowed.join(', ') : '(nenhuma — estado final)'
      }`,
    );
  }
}

// ─── Input shape ──────────────────────────────────────────────────────
//
// Caller supplies everything EXCEPT the bookkeeping fields (id/createdAt/
// updatedAt/updatedBy). Those are stamped by the service.

export type LensOrderCreateInput = Omit<
  LensOrder,
  'id' | 'createdAt' | 'updatedAt' | 'updatedBy'
>;

// ─── CRUD ─────────────────────────────────────────────────────────────

export const LensOrderService = {
  /**
   * Create a new lens order. Stamps createdAt/updatedAt and sets updatedBy =
   * createdBy initially. Returns the generated doc id.
   */
  async createOrder(
    clinicId: string,
    input: LensOrderCreateInput,
  ): Promise<string> {
    const now = Date.now();
    const payload: Omit<LensOrder, 'id'> = {
      ...input,
      clinicId,
      createdAt: now,
      updatedAt: now,
      updatedBy: input.createdBy,
    };
    const ref = await addDoc(
      colRef(clinicId),
      stripUndefined(payload as unknown as Record<string, unknown>),
    );
    return ref.id;
  },

  /** Fetch a single order. Returns null when the doc does not exist. */
  async getOrder(clinicId: string, orderId: string): Promise<LensOrder | null> {
    const snap = await getDoc(docRef(clinicId, orderId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...(snap.data() as Omit<LensOrder, 'id'>) };
  },

  /**
   * List all orders in a clinic, newest first. For bigger clinics this
   * should be paginated — the UI wraps this in a `limit=50` for now and
   * we can add startAfter/startAt in a later sprint if needed.
   */
  async listOrders(
    clinicId: string,
    options: { max?: number } = {},
  ): Promise<LensOrder[]> {
    const { max = 50 } = options;
    const q = query(
      colRef(clinicId),
      orderBy('createdAt', 'desc'),
      qLimit(max),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<LensOrder, 'id'>),
    }));
  },

  /**
   * Orders created within [startMs, endMs] (epoch ms). Used by the
   * financial reports (revenue, margin, taxes).
   */
  async listByCreatedAtRange(
    clinicId: string,
    startMs: number,
    endMs: number,
  ): Promise<LensOrder[]> {
    const q = query(
      colRef(clinicId),
      where('createdAt', '>=', startMs),
      where('createdAt', '<=', endMs),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<LensOrder, 'id'>),
    }));
  },

  /**
   * All orders for a specific patient, newest first. Used in the patient
   * detail page. Requires the composite index `(patientId, createdAt DESC)`.
   */
  async getOrdersByPatient(
    clinicId: string,
    patientId: string,
  ): Promise<LensOrder[]> {
    const q = query(
      colRef(clinicId),
      where('patientId', '==', patientId),
      orderBy('createdAt', 'desc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<LensOrder, 'id'>),
    }));
  },

  /**
   * Change status with guard. Fetches the current doc, validates the
   * transition against `ALLOWED_TRANSITIONS`, and writes the new status +
   * updatedAt/updatedBy stamps. Quando a transição é CONFIRMED→DELIVERED,
   * carimba `fulfillment.deliveredAt` se ainda não estiver definido (sem
   * sobrescrever lançamentos manuais já feitos pelo staff).
   */
  async transitionStatus(
    clinicId: string,
    orderId: string,
    to: LensOrderStatus,
    actorUid: string,
  ): Promise<void> {
    const current = await this.getOrder(clinicId, orderId);
    if (!current) throw new Error('Pedido de lente não encontrado');

    assertTransition(current.status, to);

    const now = Date.now();
    const patch: Record<string, unknown> = {
      status: to,
      updatedAt: now,
      updatedBy: actorUid,
    };

    if (to === 'DELIVERED' && !current.fulfillment?.deliveredAt) {
      patch.fulfillment = {
        ...(current.fulfillment ?? {}),
        deliveredAt: now,
      };
    }

    await updateDoc(docRef(clinicId, orderId), stripUndefined(patch));
  },

  /**
   * Atualiza um subset dos campos de `fulfillment` (merge raso). Não
   * apaga campos já gravados — passar undefined no patch é ignorado.
   * Pra zerar um campo, passe explicitamente `null` (Firestore aceita).
   *
   * Não valida o status do pedido — staff pode ajustar valor / forma de
   * pagamento / motivo de atraso mesmo em pedidos já entregues ou
   * cancelados (correções pós-fato são parte do fluxo real da ótica).
   */
  async updateFulfillment(
    clinicId: string,
    orderId: string,
    patch: Partial<LensOrderFulfillment>,
    actorUid: string,
  ): Promise<void> {
    const current = await this.getOrder(clinicId, orderId);
    if (!current) throw new Error('Pedido de lente não encontrado');

    const merged = stripUndefined({
      ...(current.fulfillment ?? {}),
      ...patch,
    }) as LensOrderFulfillment;

    await updateDoc(
      docRef(clinicId, orderId),
      stripUndefined({
        fulfillment: merged,
        updatedAt: Date.now(),
        updatedBy: actorUid,
      }),
    );
  },

  /**
   * Conveniência pro modal binocular: aplica o mesmo patch nos dois docs
   * do par em sequência. Falha rápido — se o primeiro update der erro,
   * o segundo nem é tentado e o caller decide o que fazer.
   */
  async updateFulfillmentForOrders(
    clinicId: string,
    orderIds: string[],
    patch: Partial<LensOrderFulfillment>,
    actorUid: string,
  ): Promise<void> {
    for (const id of orderIds) {
      await this.updateFulfillment(clinicId, id, patch, actorUid);
    }
  },

  /**
   * Helper: monta um `SignatureRecord` a partir do usuário logado. UI
   * passa só o uid + nome — o serviço carimba o timestamp.
   */
  buildSignature(actorUid: string, actorName: string): SignatureRecord {
    return {
      userId: actorUid,
      userName: actorName,
      signedAt: Date.now(),
    };
  },

  /**
   * Update the `notes` field in place. Does NOT allow modifying clinical
   * fields (Rx, sku, eye, etc.) — once an order is CONFIRMED the clinical
   * data is immutable; to change the Rx you cancel and create a new order.
   */
  async updateNotes(
    clinicId: string,
    orderId: string,
    notes: string,
    actorUid: string,
  ): Promise<void> {
    await updateDoc(
      docRef(clinicId, orderId),
      stripUndefined({
        notes,
        updatedAt: Date.now(),
        updatedBy: actorUid,
      }),
    );
  },
};
